import React, { useCallback, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  getTimeslotHref,
  timeslotColumns,
} from "./tableParams/timeslotColumns";
import FastAPI from "../../../api/FastAPI";
import useFASTLayout from "../../../hooks/useFASTLayout";
import CreateButton from "../../common/CreateButton";
import DataTable from "../../dataTable/DataTable";
import { timeslotPath } from "../../routes/routes";
import useFastRowActionList from "../useFastRowActionList";

import type { JSX } from "react";

import type {
  GetTimeslotResponse,
  TimeslotListItem,
} from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";
import type { FastRowRights } from "../useFastRowActionList.types";

function selectTimeslotRights(details: GetTimeslotResponse): FastRowRights {
  return details;
}

function TimeslotList(): JSX.Element {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { t } = useTranslation("editor-fast-channel");

  const queryClient = useQueryClient();

  useFASTLayout({
    title: t("timeslots"),
  });

  const getTimeslotListQuery = FastAPI.generateQuery({
    fn: FastAPI.getTimeslotList,
    params: [channelId],
  });

  const { isLoading, isError, refetch, data } = useQuery(getTimeslotListQuery);

  const { mutate: deleteTimeslot } = useMutation({
    mutationFn: async (timeslotId: string) =>
      FastAPI.deleteTimeslot({ channelId, timeslotId }),
    onSuccess: () => {
      toast.success(t("timeslotDeleteSuccess"));
      queryClient.invalidateQueries(getTimeslotListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("timeslotDeleteError"));
      console.error(e);
    },
  });

  const { mutate: archiveTimeslot } = useMutation({
    mutationFn: async (timeslotId: string) =>
      FastAPI.archiveTimeslot({ channelId, timeslotId }),
    onSuccess: () => {
      toast.success(t("timeslotArchiveSuccess"));
      queryClient.invalidateQueries(getTimeslotListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("timeslotArchiveError"));
      console.error(e);
    },
  });

  const { mutate: duplicateTimeslot } = useMutation({
    mutationFn: async (timeslotId: string) =>
      FastAPI.duplicateTimeslot({ channelId, timeslotId }),
    onSuccess: () => {
      toast.success(t("timeslotDuplicateSuccess"));
      queryClient.invalidateQueries(getTimeslotListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("timeslotDuplicateError"));
      console.error(e);
    },
  });

  const getDetailsQuery = useCallback(
    (timeslot: TimeslotListItem) =>
      FastAPI.generateQuery({
        fn: FastAPI.getTimeslot,
        params: [{ channelId, timeslotId: timeslot.uuid }],
      }),
    [channelId],
  );

  const getEditPath = useCallback(
    (timeslot: TimeslotListItem): string =>
      getTimeslotHref(channelId, timeslot),
    [channelId],
  );

  const loadActions = useFastRowActionList({
    labelPrefix: "timeslot",
    getDetailsQuery,
    selectRights: selectTimeslotRights,
    getEditPath,
    onDuplicate: duplicateTimeslot,
    onArchive: archiveTimeslot,
    onDelete: deleteTimeslot,
  });

  const getRowHref = useCallback(
    (timeslot: TimeslotListItem): string =>
      getTimeslotHref(channelId, timeslot),
    [channelId],
  );

  const columnList = useMemo(
    () => timeslotColumns({ t, channelId, loadActions }),
    [t, channelId, loadActions],
  );

  const createTimeslotButton = (
    <CreateButton
      href={timeslotPath(channelId, "new")}
      label={t("createTimeslot")}
    />
  );

  return (
    <div className="w-full min-w-0">
      <DataTable
        columns={columnList}
        data={data?.data ?? []}
        defaultSorting={{ id: "updated_at", desc: true }}
        getRowHref={getRowHref}
        headerActions={createTimeslotButton}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("timeslotListTitle")}
      />
    </div>
  );
}

export default TimeslotList;
